/**
 * PreviewWorkDesk — pixel-faithful render of the ClaimSync Workspace "Work Desk"
 * surface, wired to REAL report data, with a light/dark toggle.
 *
 * The Workspace template was supplied in Daylight only; the dark variant is
 * derived from the canonical dark token set in `index.css`, so it matches the
 * design language of the other dark templates. The light render is pixel-
 * identical to the approved Daylight template.
 *
 * Mounted at /preview/workdesk. Self-contained — does NOT touch /dashboard, the
 * feature flags, or any working route. Design-review surface only.
 *
 * Shared tokens/helpers live in ./preview/previewTheme.
 */

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import {
  SERIF, BODY, MONO, PREVIEW_ZOOM, Palette, ChipKind, useTheme, ThemeToggle, chipStyle, fmtUSD, relTime,
} from './preview/previewTheme';

interface ApiReport {
  id: string;
  client_name?: string;
  business_type?: string;
  event_type?: string;
  status?: string;
  law_firm?: string;
  result?: { final_claim_amount?: number; lost_revenue?: number };
  created_at?: string;
  updated_at?: string;
}

interface DeskCase {
  id: string;
  claim: string;
  client: string;
  stageLabel: string;
  stageKind: ChipKind;
  exposure: number;
  statusLabel: string;
  statusKind: ChipKind;
  updated: string;
  needsAction: boolean;
}

function classify(status?: string): {
  stageLabel: string; stageKind: ChipKind; statusLabel: string; statusKind: ChipKind; needsAction: boolean;
} {
  const s = (status || '').toLowerCase();
  if (s === 'completed' || s === 'complete' || s === 'finalized') {
    return { stageLabel: 'Deliverables', stageKind: 'green', statusLabel: 'Complete', statusKind: 'green', needsAction: false };
  }
  if (s === 'in_progress' || s === 'analyzing' || s === 'processing') {
    return { stageLabel: 'Analysis', stageKind: 'orange', statusLabel: 'In progress', statusKind: 'orange', needsAction: true };
  }
  if (s === 'review' || s === 'in_review') {
    return { stageLabel: 'Analysis', stageKind: 'orange', statusLabel: 'Needs review', statusKind: 'orange', needsAction: true };
  }
  if (s === 'draft') {
    return { stageLabel: 'Intake', stageKind: 'ghost', statusLabel: 'Docs pending', statusKind: 'ghost', needsAction: false };
  }
  return { stageLabel: 'Data', stageKind: 'neutral', statusLabel: status || 'Unknown', statusKind: 'neutral', needsAction: false };
}

// ----------------------------------------------------------------------------
// left rail
// ----------------------------------------------------------------------------
const NAV_GROUPS: { group: string; items: { id: string; label: string; icon: string; badge?: string; to?: string }[] }[] = [
  { group: 'Work Desk', items: [{ id: 'desk', label: 'Work Desk', icon: '◧', to: '/preview/workdesk' }] },
  { group: 'Cases', items: [
    { id: 'cases', label: 'All Claims', icon: '▤', to: '/dashboard' },
    { id: 'new', label: 'New Claim', icon: '＋', to: '/new-claim' },
  ] },
  { group: 'Review', items: [
    { id: 'engine', label: 'Engine Review', icon: '◈', to: '/queue' },
    { id: 'anom', label: 'Anomalies', icon: '◬', badge: '', to: '/queue' },
    { id: 'attest', label: 'Attestations', icon: '✺', badge: '', to: '/queue' },
  ] },
  { group: 'Deliverables', items: [
    { id: 'gen', label: 'Generators', icon: '❏', to: '/dashboard' },
    { id: 'export', label: 'Export Center', icon: '⤓', to: '/dashboard' },
  ] },
  { group: 'Intelligence', items: [
    { id: 'method', label: 'Methodology', icon: '§', to: '/dashboard' },
    { id: 'activity', label: 'Activity Log', icon: '≣', to: '/admin/activity-log' },
  ] },
];

function LeftRail({ P, navigate, queueBadge }: { P: Palette; navigate: (to: string) => void; queueBadge: number }) {
  return (
    <aside style={{ width: 240, flex: '0 0 240px', background: P.pageBg, display: 'flex', flexDirection: 'column', height: '100%', borderRight: `1px solid ${P.railHairline}` }}>
      <div style={{ padding: '20px 20px 16px', display: 'flex', alignItems: 'center', gap: 11 }}>
        <div style={{ width: 30, height: 30, borderRadius: 8, background: P.orange, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 30px' }}>
          <div style={{ width: 12, height: 12, border: '2.5px solid #fefefe', borderRadius: '50%' }} />
        </div>
        <div style={{ lineHeight: 1 }}>
          <div style={{ fontFamily: SERIF, color: P.textPrimary, fontSize: 20, letterSpacing: '0.2px' }}>The Figure Co.</div>
          <div style={{ fontSize: 9.5, letterSpacing: '1.4px', color: P.textMuted, textTransform: 'uppercase', marginTop: 2 }}>Forensic BI Platform</div>
        </div>
      </div>

      <nav style={{ flex: 1, overflowY: 'auto', padding: '6px 0 16px' }}>
        {NAV_GROUPS.map((grp) => (
          <div key={grp.group} style={{ margin: '10px 0 2px' }}>
            <div style={{ fontSize: 10, letterSpacing: '1.3px', color: P.textMuted, textTransform: 'uppercase', fontWeight: 600, padding: '6px 22px 4px' }}>{grp.group}</div>
            {grp.items.map((item) => {
              const active = item.id === 'desk';
              const badge = item.id === 'anom' || item.id === 'attest' ? (queueBadge ? String(queueBadge) : '') : item.badge;
              return (
                <a
                  key={item.id}
                  onClick={() => item.to && navigate(item.to)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 11, padding: '8px 18px 8px 19px',
                    fontSize: 13.5, fontWeight: 500, cursor: 'pointer', textDecoration: 'none',
                    borderLeft: `3px solid ${active ? P.orange : 'transparent'}`,
                    color: active ? P.textPrimary : P.textLabel,
                    background: active ? P.elevBg : 'transparent',
                  }}
                  onMouseEnter={(e) => { if (!active) { e.currentTarget.style.background = P.elevBg; e.currentTarget.style.color = P.textPrimary; } }}
                  onMouseLeave={(e) => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = P.textLabel; } }}
                >
                  <span style={{ fontSize: 14, width: 18, textAlign: 'center', opacity: 0.9 }}>{item.icon}</span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {badge ? (
                    <span style={{ fontSize: 10.5, fontWeight: 600, background: P.orange, color: P.onOrange, borderRadius: 999, padding: '1px 7px', minWidth: 18, textAlign: 'center' }}>{badge}</span>
                  ) : null}
                </a>
              );
            })}
          </div>
        ))}
      </nav>

      <div style={{ padding: '12px 16px', borderTop: `1px solid ${P.railHairline}`, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 32, height: 32, borderRadius: '50%', background: P.elevBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.textBody, fontWeight: 600, fontSize: 12.5, flex: '0 0 32px' }}>TM</div>
        <div style={{ flex: 1, lineHeight: 1.25, minWidth: 0 }}>
          <div style={{ color: P.textPrimary, fontSize: 13, fontWeight: 500 }}>Tyler Mercer</div>
          <div style={{ color: P.textLabel, fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: P.orange, display: 'inline-block' }} />Internal Accountant
          </div>
        </div>
        <span style={{ color: P.textMuted, fontSize: 15 }}>⌄</span>
      </div>
    </aside>
  );
}

// ----------------------------------------------------------------------------
// topbar
// ----------------------------------------------------------------------------
function Topbar({ P, theme, onToggleTheme, onCmdk }: { P: Palette; theme: 'light' | 'dark'; onToggleTheme: () => void; onCmdk: () => void }) {
  return (
    <header style={{ height: 58, flex: '0 0 58px', background: P.pageBg, borderBottom: `1px solid ${P.railHairline}`, display: 'flex', alignItems: 'center', padding: '0 22px', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: P.textLabel, minWidth: 0, flexShrink: 1, overflow: 'hidden' }}>
        <span style={{ color: P.textPrimary, fontWeight: 500 }}>Work Desk</span>
      </div>
      <div
        onClick={onCmdk}
        style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 9, background: P.elevBg, border: `1px solid ${P.cardBorder}`, borderRadius: 9, padding: '7px 12px', width: 300, cursor: 'text', color: P.textMuted, fontSize: 13 }}
      >
        <span style={{ fontSize: 13 }}>⌕</span>
        <span style={{ flex: 1 }}>Search cases, tools, actions…</span>
        <span style={{ fontFamily: MONO, fontSize: 11, background: P.kbdBg, border: `1px solid ${P.controlBorder}`, borderRadius: 5, padding: '1px 6px', color: P.textLabel }}>⌘K</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: P.elevBg, border: `1px solid ${P.cardBorder}`, borderRadius: 999, padding: '5px 12px', fontSize: 12, color: P.textBody, fontWeight: 500, whiteSpace: 'nowrap' }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: P.orange, display: 'inline-block' }} />Accountant lens
      </div>
      <ThemeToggle P={P} theme={theme} onToggle={onToggleTheme} />
    </header>
  );
}

function MetricCard({ P, label, value, sub, accent }: { P: Palette; label: string; value: string; sub: string; accent: boolean }) {
  return (
    <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderLeft: `3px solid ${accent ? P.orange : P.accentNeutral}`, borderRadius: 13, padding: '18px 20px' }}>
      <div style={{ fontSize: 12.5, color: P.textLabel, fontWeight: 500, marginBottom: 10 }}>{label}</div>
      <div style={{ fontFamily: MONO, fontSize: 26, color: accent ? P.orangeText : P.textPrimary, fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      <div style={{ fontSize: 11.5, color: P.textMuted, marginTop: 7 }}>{sub}</div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// main component
// ----------------------------------------------------------------------------
export default function PreviewWorkDesk() {
  const navigate = useNavigate();
  const { theme, P, toggle } = useTheme();
  const [reports, setReports] = useState<ApiReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [cmdk, setCmdk] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.get('/api/v1/reports?limit=200', { showErrorToast: false });
        const list: ApiReport[] = Array.isArray(data?.reports) ? data.reports : Array.isArray(data) ? data : [];
        if (alive) setReports(list);
      } catch {
        if (alive) setReports([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setCmdk((v) => !v); }
      if (e.key === 'Escape') setCmdk(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const cases: DeskCase[] = useMemo(() => reports.map((r) => {
    const c = classify(r.status);
    return {
      id: r.id,
      claim: (r.id || '').slice(0, 8).toUpperCase(),
      client: r.client_name || 'Unnamed Claim',
      stageLabel: c.stageLabel,
      stageKind: c.stageKind,
      exposure: r.result?.final_claim_amount || 0,
      statusLabel: c.statusLabel,
      statusKind: c.statusKind,
      updated: relTime(r.updated_at || r.created_at),
      needsAction: c.needsAction,
    };
  }), [reports]);

  const totalExposure = useMemo(() => cases.reduce((s, c) => s + c.exposure, 0), [cases]);
  const needsActionCount = useMemo(() => cases.filter((c) => c.needsAction).length, [cases]);
  const completeCount = useMemo(() => cases.filter((c) => c.statusKind === 'green').length, [cases]);
  const queue = useMemo(() => cases.filter((c) => c.needsAction).slice(0, 4), [cases]);
  const recents = useMemo(() => cases.slice(0, 4), [cases]);

  const goCase = useCallback((id: string) => navigate(`/report/${id}`), [navigate]);

  return (
    <div style={{ zoom: PREVIEW_ZOOM, display: 'flex', height: '100vh', width: '100%', overflow: 'hidden', background: P.pageBg, fontFamily: BODY, color: P.textBody }}>
      <LeftRail P={P} navigate={navigate} queueBadge={needsActionCount} />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
        <Topbar P={P} theme={theme} onToggleTheme={toggle} onCmdk={() => setCmdk(true)} />

        <main style={{ flex: 1, overflow: 'auto', background: P.pageBg }}>
          <div style={{ padding: '28px 32px 48px', maxWidth: 1320, margin: '0 auto' }}>
            {/* greeting row */}
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 22, gap: 20, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 12, letterSpacing: '1.4px', color: P.orangeText, textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 }}>Internal Accountant · Work Desk</div>
                <h1 style={{ fontFamily: SERIF, fontSize: 32, color: P.textPrimary, margin: 0, fontWeight: 400 }}>Good morning, Tyler</h1>
                <p style={{ margin: '6px 0 0', color: P.textLabel, fontSize: 14.5 }}>
                  You have <strong style={{ color: P.orangeText }}>{needsActionCount} {needsActionCount === 1 ? 'claim' : 'claims'}</strong> in active analysis and <strong style={{ color: P.textPrimary }}>{cases.length}</strong> total on your desk.
                </p>
              </div>
              <button
                onClick={() => navigate('/new-claim')}
                style={{ background: P.orange, color: P.onOrange, border: 'none', borderRadius: 10, padding: '11px 20px', fontSize: 14, fontWeight: 600, fontFamily: BODY, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}
                onMouseEnter={(e) => { e.currentTarget.style.background = P.orangeHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = P.orange; }}
              >＋ New Claim</button>
            </div>

            {/* metric rail */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
              <MetricCard P={P} label="Active claims" value={loading ? '—' : String(cases.length)} sub={`${cases.length} total on desk`} accent={false} />
              <MetricCard P={P} label="In active analysis" value={loading ? '—' : String(needsActionCount)} sub="awaiting your review" accent />
              <MetricCard P={P} label="Completed" value={loading ? '—' : String(completeCount)} sub="deliverables ready" accent={false} />
              <MetricCard P={P} label="Total exposure" value={loading ? '—' : fmtUSD(totalExposure)} sub="all active claims" accent={false} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start' }}>
              {/* active claims table */}
              <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${P.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h2 style={{ fontFamily: SERIF, fontSize: 18, color: P.textPrimary, margin: 0, fontWeight: 400 }}>Active Claims</h2>
                  <div style={{ display: 'flex', gap: 7 }}>
                    <span style={{ fontSize: 12, color: P.textBody, background: P.elevBg, border: `1px solid ${P.controlBorder}`, borderRadius: 7, padding: '5px 11px', cursor: 'pointer' }}>All stages ⌄</span>
                    <span style={{ fontSize: 12, color: P.textBody, background: P.elevBg, border: `1px solid ${P.controlBorder}`, borderRadius: 7, padding: '5px 11px', cursor: 'pointer' }}>Mine ⌄</span>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1.3fr 1.5fr 1fr 0.9fr 1.1fr 0.7fr', padding: '9px 20px', borderBottom: `1px solid ${P.divider}`, fontSize: 11, letterSpacing: '0.6px', textTransform: 'uppercase', color: P.textMuted, fontWeight: 600 }}>
                  <span>Claim No.</span><span>Insured</span><span>Stage</span><span style={{ textAlign: 'right' }}>Exposure</span><span>Status</span><span style={{ textAlign: 'right' }}>Updated</span>
                </div>

                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <div key={i} style={{ height: 49, borderBottom: `1px solid ${P.rowDivider}`, background: i % 2 ? P.cardBg : P.cardAltBg }} />
                  ))
                ) : cases.length === 0 ? (
                  <div style={{ padding: '48px 20px', textAlign: 'center', color: P.textMuted, fontSize: 14 }}>No active claims on your desk yet.</div>
                ) : (
                  cases.map((c) => (
                    <div
                      key={c.id}
                      onClick={() => goCase(c.id)}
                      style={{ display: 'grid', gridTemplateColumns: '1.3fr 1.5fr 1fr 0.9fr 1.1fr 0.7fr', padding: '14px 20px', borderBottom: `1px solid ${P.rowDivider}`, alignItems: 'center', cursor: 'pointer', background: c.needsAction ? P.orangeRowTint : 'transparent' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = P.hoverBg; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = c.needsAction ? P.orangeRowTint : 'transparent'; }}
                    >
                      <span style={{ fontFamily: MONO, fontSize: 12.5, color: P.textBody, fontWeight: 500 }}>{c.claim}</span>
                      <span style={{ fontSize: 13.5, color: P.textPrimary, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 8 }}>{c.client}</span>
                      <span><span style={chipStyle(c.stageKind, P)}>{c.stageLabel}</span></span>
                      <span style={{ fontFamily: MONO, fontSize: 13, color: P.textBody, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.exposure ? fmtUSD(c.exposure) : '—'}</span>
                      <span><span style={chipStyle(c.statusKind, P)}>{c.statusLabel}</span></span>
                      <span style={{ fontSize: 12, color: P.textMuted, textAlign: 'right' }}>{c.updated}</span>
                    </div>
                  ))
                )}
              </div>

              {/* right column: queue + recents */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', borderBottom: `1px solid ${P.divider}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: P.orange }} />
                    <h3 style={{ fontFamily: SERIF, fontSize: 16, color: P.textPrimary, margin: 0, fontWeight: 400, flex: 1 }}>My Work Queue</h3>
                    <span style={{ fontSize: 11, color: P.textMuted }}>{queue.length}</span>
                  </div>
                  {queue.length === 0 ? (
                    <div style={{ padding: '18px', fontSize: 12.5, color: P.textMuted }}>Nothing awaiting review.</div>
                  ) : queue.map((q) => (
                    <div key={q.id} onClick={() => goCase(q.id)} style={{ padding: '12px 18px', borderBottom: `1px solid ${P.rowDivider}`, cursor: 'pointer', display: 'flex', gap: 11, alignItems: 'flex-start' }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = P.hoverBg; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: P.orange, marginTop: 5, flex: '0 0 8px' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: P.textPrimary, fontWeight: 500, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Review analysis — {q.client}</div>
                        <div style={{ fontSize: 11.5, color: P.textMuted, marginTop: 2 }}>{q.claim} · {q.updated}</div>
                      </div>
                    </div>
                  ))}
                </div>

                <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', borderBottom: `1px solid ${P.divider}` }}>
                    <h3 style={{ fontFamily: SERIF, fontSize: 16, color: P.textPrimary, margin: 0, fontWeight: 400 }}>Recents &amp; Pinned</h3>
                  </div>
                  {recents.length === 0 ? (
                    <div style={{ padding: '18px', fontSize: 12.5, color: P.textMuted }}>No recent activity.</div>
                  ) : recents.map((r) => (
                    <div key={r.id} onClick={() => goCase(r.id)} style={{ padding: '11px 18px', borderBottom: `1px solid ${P.rowDivider}`, cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, color: P.textBody }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = P.hoverBg; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{ color: P.textMuted, fontSize: 12 }}>◈</span>
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.client}</span>
                      <span style={{ fontSize: 11, color: P.textGhost }}>{r.updated}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* command palette */}
      {cmdk && (
        <div onClick={() => setCmdk(false)} style={{ position: 'fixed', inset: 0, background: P.overlayBg, backdropFilter: 'blur(3px)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: '14vh', zIndex: 50 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ width: 560, maxWidth: '92vw', background: P.cmdkPanelBg, borderRadius: 16, boxShadow: `0 24px 60px ${P.shadow}`, overflow: 'hidden', border: `1px solid ${P.controlBorder}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '16px 20px', borderBottom: `1px solid ${P.divider}` }}>
              <span style={{ fontSize: 16, color: P.textMuted }}>⌕</span>
              <input autoFocus placeholder="Search cases, tools, actions…" style={{ flex: 1, border: 'none', outline: 'none', fontFamily: BODY, fontSize: 15, color: P.textPrimary, background: 'transparent' }} />
              <span style={{ fontFamily: MONO, fontSize: 11, background: P.kbdBg, border: `1px solid ${P.controlBorder}`, borderRadius: 5, padding: '2px 7px', color: P.textLabel }}>esc</span>
            </div>
            <div style={{ padding: 8 }}>
              <div style={{ fontSize: 10.5, letterSpacing: '0.8px', textTransform: 'uppercase', color: P.textMuted, fontWeight: 600, padding: '8px 12px 4px' }}>Jump to</div>
              {[
                { icon: '◧', label: 'Work Desk', to: '/preview/workdesk' },
                { icon: '▤', label: 'All Claims', to: '/dashboard' },
                { icon: '＋', label: 'New Claim', to: '/new-claim' },
                { icon: '⤓', label: 'Export Center', to: '/dashboard' },
              ].map((c) => (
                <div key={c.label} onClick={() => { setCmdk(false); navigate(c.to); }} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 9, cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = P.cmdkItemHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  <span style={{ width: 26, height: 26, borderRadius: 7, background: P.cmdkItemHover, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: P.textBody }}>{c.icon}</span>
                  <span style={{ flex: 1, fontSize: 13.5, color: P.textBody }}>{c.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
